// TFVF Practical Assessment — manager records the 10-criteria evaluation
// for a staff member. UI phases:
//   list mode      — manager's assessments + "New" button
//   editor mode    — 10 criteria with per-criterion score + evidence,
//                    strengths, primary coaching behaviour, governance
//                    flag, sign-off
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Plus, ShieldCheck, AlertTriangle, ArrowLeft, Save, CheckCircle2,
} from 'lucide-react';
import { API, authHeaders } from './tfvfShared';
import { useAuth } from '../../../contexts/AuthContext';

const TfvfPractical = () => {
  const { token } = useAuth();
  const [criteria, setCriteria] = useState([]);
  const [passScore, setPassScore] = useState(70);
  const [maxScore, setMaxScore] = useState(100);
  const [teamRows, setTeamRows] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newTargetId, setNewTargetId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, dash] = await Promise.all([
        axios.get(`${API}/tfvf/practical/criteria`, { headers: authHeaders(token) }),
        axios.get(`${API}/tfvf/manager/dashboard`, { headers: authHeaders(token) }),
      ]);
      setCriteria(c.data?.criteria || []);
      setPassScore(c.data?.pass_score || 70);
      setMaxScore(c.data?.max_score || 100);
      const rows = dash.data?.rows || [];
      setTeamRows(rows);

      // Aggregate assessments across everyone in scope.
      const perUser = await Promise.all(rows.map(r =>
        axios.get(`${API}/tfvf/practical/assessments/user/${r.user_id}`, { headers: authHeaders(token) })
          .then(res => res.data?.assessments || []).catch(() => []),
      ));
      setAssessments(perUser.flat());
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) loadAll(); }, [token, loadAll]);

  const openAssessment = async (aid) => {
    setActiveId(aid);
    const found = assessments.find(a => a.id === aid);
    setDetail(found || null);
  };

  const patchDetail = (patch) => setDetail(d => ({ ...d, ...patch }));

  const patchScore = (key, val) => {
    const v = val === '' ? null : Number(val);
    setDetail(d => ({ ...d, scores: { ...d.scores, [key]: v } }));
  };
  const patchEvidence = (key, val) => setDetail(d => ({ ...d, evidence: { ...d.evidence, [key]: val } }));

  const totalScore = detail
    ? Object.values(detail.scores || {}).reduce((s, v) => s + (Number(v) || 0), 0)
    : 0;

  const save = async (thenSignoff = false) => {
    if (!detail) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/tfvf/practical/assessments/${detail.id}`, {
        scores: detail.scores,
        evidence: detail.evidence,
        strengths: detail.strengths,
        primary_coaching_behaviour: detail.primary_coaching_behaviour,
        approved_words_to_practise: detail.approved_words_to_practise,
        words_to_avoid: detail.words_to_avoid,
        governance_failure: detail.governance_failure,
        governance_note: detail.governance_note,
        reassessment_required: detail.reassessment_required,
      }, { headers: authHeaders(token) });
      toast.success('Saved');
      if (thenSignoff) {
        const r = await axios.post(`${API}/tfvf/practical/assessments/${detail.id}/signoff`, {}, { headers: authHeaders(token) });
        toast.success(`Signed off — ${r.data.result} (${r.data.total_score}/100)`);
        setDetail(r.data);
      }
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const createNew = async () => {
    if (!newTargetId) { toast.info('Pick a staff member'); return; }
    try {
      const r = await axios.post(`${API}/tfvf/practical/assessments`, { user_id: newTargetId }, { headers: authHeaders(token) });
      toast.success('Draft created');
      setShowNew(false);
      await loadAll();
      openAssessment(r.data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--tf-forest)]/50" /></div>;

  // ── Editor view ─────────────────────────────────────────
  if (detail) {
    const locked = detail.status === 'signed_off';
    return (
      <div className="space-y-4" data-testid="tfvf-practical-editor">
        <button onClick={() => { setActiveId(null); setDetail(null); }} className="text-xs text-[color:var(--tf-muted)] hover:text-[color:var(--tf-forest)] inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> All assessments
        </button>

        <div className="tf-card rounded-xl p-5">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <p className="tf-eyebrow">Practical assessment</p>
              <h2 className="text-xl font-bold text-[color:var(--tf-forest)] mt-1">
                {detail.user_name} <span className="text-[color:var(--tf-muted)] text-sm">· by {detail.manager_name}</span>
              </h2>
              {locked && (
                <p className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> Signed off {new Date(detail.manager_signoff_at).toLocaleString()} · locked
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="tf-eyebrow">Total</p>
              <p className={`text-3xl font-bold ${totalScore >= passScore ? 'text-emerald-700' : 'text-amber-700'}`}>
                {totalScore.toFixed(1)}<span className="text-lg text-[color:var(--tf-muted)]">/{maxScore}</span>
              </p>
              <p className="text-[10px] text-[color:var(--tf-muted)]">Pass ≥ {passScore}</p>
            </div>
          </div>
        </div>

        {/* 10 criteria */}
        <div className="tf-card rounded-xl p-5 space-y-3">
          {criteria.map((c, i) => (
            <div key={c.key} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start border-b border-[color:var(--tf-forest)]/10 pb-3 last:border-0" data-testid={`practical-crit-${c.key}`}>
              <div className="md:col-span-6">
                <p className="text-xs tf-eyebrow">Criterion {i + 1}</p>
                <p className="text-sm font-semibold text-[color:var(--tf-forest)]">{c.name}</p>
                <p className="text-[11px] text-[color:var(--tf-muted)]">Weight: {c.weight}pt</p>
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] tf-eyebrow">Score</label>
                <input
                  type="number" min={0} max={c.weight} step={0.5}
                  value={detail.scores?.[c.key] ?? ''}
                  disabled={locked}
                  onChange={e => patchScore(c.key, e.target.value)}
                  placeholder={`0..${c.weight}`}
                  className="w-full mt-0.5 border border-[color:var(--tf-forest)]/15 rounded px-2 py-1 text-sm bg-white disabled:bg-[color:var(--tf-parchment)]"
                  data-testid={`practical-score-${c.key}`}
                />
              </div>
              <div className="md:col-span-4">
                <label className="text-[10px] tf-eyebrow">Evidence</label>
                <textarea
                  value={detail.evidence?.[c.key] || ''}
                  disabled={locked}
                  onChange={e => patchEvidence(c.key, e.target.value)}
                  rows={2}
                  placeholder="Quote or paraphrase from the call…"
                  className="w-full mt-0.5 border border-[color:var(--tf-forest)]/15 rounded px-2 py-1 text-xs bg-white disabled:bg-[color:var(--tf-parchment)]"
                  data-testid={`practical-evidence-${c.key}`}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Meta */}
        <div className="tf-card rounded-xl p-5 space-y-3">
          <div>
            <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Strengths observed</label>
            <textarea value={detail.strengths || ''} disabled={locked} onChange={e => patchDetail({ strengths: e.target.value })} rows={2} className="w-full mt-1 border border-[color:var(--tf-forest)]/15 rounded-lg px-3 py-2 text-sm bg-white disabled:bg-[color:var(--tf-parchment)]" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Primary coaching behaviour (one focus)</label>
            <input value={detail.primary_coaching_behaviour || ''} disabled={locked} onChange={e => patchDetail({ primary_coaching_behaviour: e.target.value })} placeholder="e.g. Green Brain — confirm caller's own words before Linking" className="w-full mt-1 border border-[color:var(--tf-forest)]/15 rounded-lg px-3 py-2 text-sm bg-white disabled:bg-[color:var(--tf-parchment)]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Approved words to practise (comma-sep)</label>
              <input
                value={(detail.approved_words_to_practise || []).join(', ')}
                disabled={locked}
                onChange={e => patchDetail({ approved_words_to_practise: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })}
                className="w-full mt-1 border border-[color:var(--tf-forest)]/15 rounded-lg px-3 py-2 text-sm bg-white disabled:bg-[color:var(--tf-parchment)]"
                placeholder="Let's get started, based on that…"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Words to avoid</label>
              <input
                value={(detail.words_to_avoid || []).join(', ')}
                disabled={locked}
                onChange={e => patchDetail({ words_to_avoid: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })}
                className="w-full mt-1 border border-[color:var(--tf-forest)]/15 rounded-lg px-3 py-2 text-sm bg-white disabled:bg-[color:var(--tf-parchment)]"
                placeholder="honestly, obviously, no worries"
              />
            </div>
          </div>
          <div>
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={!!detail.governance_failure} disabled={locked} onChange={e => patchDetail({ governance_failure: e.target.checked })} className="accent-rose-600" data-testid="practical-governance-flag" />
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              Governance failure occurred
            </label>
            {detail.governance_failure && (
              <textarea value={detail.governance_note || ''} disabled={locked} onChange={e => patchDetail({ governance_note: e.target.value })} rows={2} className="w-full mt-2 border border-rose-300 rounded-lg px-3 py-2 text-sm bg-rose-50 disabled:bg-rose-100" placeholder="Describe the governance issue…" />
            )}
          </div>
          <div>
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={!!detail.reassessment_required} disabled={locked} onChange={e => patchDetail({ reassessment_required: e.target.checked })} />
              Require reassessment
            </label>
          </div>
        </div>

        {/* Actions */}
        {!locked && (
          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={() => save(false)} disabled={saving} className="rounded-lg px-3 py-2 text-sm text-[color:var(--tf-forest)] border border-[color:var(--tf-forest)]/20 hover:bg-[color:var(--tf-parchment)] inline-flex items-center gap-1 disabled:opacity-50" data-testid="practical-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save draft
            </button>
            <button onClick={() => save(true)} disabled={saving} className="tf-btn-primary rounded-lg px-3 py-2 text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50" data-testid="practical-signoff-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Sign off
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="tfvf-practical-page">
      <div className="tf-card rounded-xl p-5 flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="tf-eyebrow">Practical assessments</p>
          <h2 className="text-xl font-bold text-[color:var(--tf-forest)] mt-1">Score staff against the 10 approved criteria</h2>
          <p className="text-xs text-[color:var(--tf-muted)] mt-1">Pass ≥ {passScore}/100 with no governance failure to move certification forward.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="tf-btn-gold rounded-lg px-3 py-2 text-sm font-semibold inline-flex items-center gap-1" data-testid="practical-new-btn">
          <Plus className="w-4 h-4" /> New assessment
        </button>
      </div>

      {showNew && (
        <div className="tf-card rounded-xl p-5 space-y-3" data-testid="practical-new-form">
          <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Staff member</label>
          <select value={newTargetId} onChange={e => setNewTargetId(e.target.value)} className="w-full border border-[color:var(--tf-forest)]/15 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">Pick a staff member</option>
            {teamRows.map(r => <option key={r.user_id} value={r.user_id}>{r.name || r.email} ({r.site || 'no site'})</option>)}
          </select>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowNew(false)} className="text-sm text-[color:var(--tf-muted)] hover:text-[color:var(--tf-forest)] px-3 py-2">Cancel</button>
            <button onClick={createNew} className="tf-btn-primary rounded-lg px-3 py-2 text-sm font-semibold" data-testid="practical-new-submit">Create draft</button>
          </div>
        </div>
      )}

      <div className="tf-card rounded-xl">
        {assessments.length === 0 ? (
          <p className="text-center text-sm text-[color:var(--tf-muted)] italic py-8">No assessments yet. Click New assessment to score your first call.</p>
        ) : (
          <div className="divide-y divide-[color:var(--tf-forest)]/10">
            {assessments.map(a => (
              <button key={a.id} onClick={() => openAssessment(a.id)} className="w-full text-left px-4 py-3 hover:bg-[color:var(--tf-parchment)] flex items-center gap-3" data-testid={`practical-row-${a.id}`}>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[color:var(--tf-forest)]">{a.user_name}</p>
                  <p className="text-xs text-[color:var(--tf-muted)]">
                    {new Date(a.updated_at || a.created_at).toLocaleString()} · by {a.manager_name}
                  </p>
                </div>
                <div className="text-right">
                  {a.status === 'signed_off' ? (
                    <span className={`inline-flex items-center gap-1 text-xs font-bold ${a.result === 'passed' ? 'text-emerald-700' : 'text-rose-700'}`}>
                      <CheckCircle2 className="w-3 h-3" /> {a.total_score}/100
                    </span>
                  ) : (
                    <span className="text-xs text-amber-700 font-semibold">Draft</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TfvfPractical;
